import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FC, memo, useState } from 'react';
import {
  CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY,
  CONVENIENCE_DISPATCH_FIXED_ROUTE_DETAIL_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchFixedRoutes.query';
import {
  useOverwriteFixedRouteSnapshotMutation,
  useSaveFixedRouteSnapshotMutation,
} from 'src/hooks/useConvenienceDispatchMutation';

type Props = {
  open: boolean;
  onClose: () => void;
  planningDate: string;
  currentFixedRouteId: number | null;
  onFixedRouteCreated: (newFixedRouteId: number) => void;
  /** 'new' なら名前入力＋新規保存、'overwrite' なら既存コースを上書き保存 */
  mode?: 'new' | 'overwrite';
};

const FixedRouteSaveDialog: FC<Props> = memo(
  ({ open, onClose, planningDate, currentFixedRouteId, onFixedRouteCreated, mode = 'new' }) => {
    const [name, setName] = useState('');
    const queryClient = useQueryClient();

    const saveAsNew = useSaveFixedRouteSnapshotMutation();
    const overwrite = useOverwriteFixedRouteSnapshotMutation();

    const isPending = saveAsNew.isPending || overwrite.isPending;

    const handleClose = () => {
      setName('');
      onClose();
    };

    const handleSaveAsNew = () => {
      if (name.trim() === '') return;
      saveAsNew.mutate(
        { planningDate, name: name.trim() },
        {
          onSuccess: (newRoute) => {
            void queryClient.invalidateQueries({
              queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY],
            });
            onFixedRouteCreated(newRoute.id);
            handleClose();
          },
        }
      );
    };

    const handleOverwrite = () => {
      if (currentFixedRouteId == null) return;
      overwrite.mutate(
        { planningDate, fixedRouteId: currentFixedRouteId },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY],
            });
            void queryClient.invalidateQueries({
              queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTE_DETAIL_QUERY_KEY, currentFixedRouteId],
            });
            handleClose();
          },
        }
      );
    };

    const title = mode === 'overwrite' ? '固定コースを上書き保存' : '固定コースとして保存';

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {mode === 'new' ? (
            <TextField
              label="コース名"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mt: 1 }}
              disabled={isPending}
            />
          ) : (
            <Box sx={{ mt: 1, fontSize: 14 }}>
              現在の配車計画で、選択中の固定コースを上書きします。よろしいですか？
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            キャンセル
          </Button>
          {mode === 'overwrite' ? (
            <Button
              variant="contained"
              onClick={handleOverwrite}
              disabled={isPending || currentFixedRouteId == null}
            >
              上書き保存
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSaveAsNew}
              disabled={isPending || name.trim() === ''}
            >
              新規保存
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

export default FixedRouteSaveDialog;
